import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import Image from "next/image";
import classNames from "classnames";
import { Product } from "../../../../stores/product-store/product-store.model";
import { ProductGridItem } from "./product-grid-item";
import throttle from "../../../../utils/throttle";
import { DEFAULT_ITEM_PER_PAGE } from "../../../../utils/constants";
import { useStores } from "../../../../stores";
import { ProductListItem } from "./product-list-item";
import { trackEvents } from "../../../../utils/analytics";
import { SellerProfile } from "../../../../stores/seller-store/seller-store.model";
import { SellerPageRouter } from "../../page-router.props";

export enum ProductLayout {
  grid,
  list,
}

interface ProductListProps {
  isReseller: boolean;
  sellerData: SellerProfile;
  mode: ProductLayout;
  products: Product[];
  onClickBuy: (product: Product) => void;
}

export const ProductList: React.FC<ProductListProps> = observer(
  ({ isReseller, sellerData, mode, products, onClickBuy }) => {
    const { productStore, cartStore, coverageAreaStore, accountStore } =
      useStores();
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [isHeaderDocked, setIsHeaderDocked] = useState<boolean>(false);
    const router: SellerPageRouter = useRouter();
    const canLoadMore = useRef(false);
    const headerInfoBar = useRef<HTMLDivElement>(null);

    useEffect(() => {
      // header div[data-section=headerInfo] from modules/store.tsx
      const headerInfoBarDOM: HTMLDivElement = document.querySelector(
        "[data-section=headerInfo]"
      );
      if (headerInfoBarDOM) {
        headerInfoBar.current = headerInfoBarDOM;
      }
    }, []);

    useEffect(() => {
      if (headerInfoBar?.current) {
        if (isHeaderDocked) {
          headerInfoBar.current.style.marginTop = `-${headerInfoBar.current.offsetHeight}px`;
        } else {
          headerInfoBar.current.style.marginTop = "";
        }
      }
    }, [isHeaderDocked]);

    const handleLoadMore = useCallback(async () => {
      const {
        seller: sellerUsername,
        fromapp,
        q,
        filter,
        category,
        order_by,
        sort_by,
      } = router.query;
      const { hasReferralCategory, trading } = accountStore.buyerAccount || {};
      try {
        setIsFetching(true);
        const offsetVal = Math.ceil(
          (products.length / DEFAULT_ITEM_PER_PAGE) * DEFAULT_ITEM_PER_PAGE
        );
        const params = {
          username: String(sellerUsername),
          offset: offsetVal,
          limit: DEFAULT_ITEM_PER_PAGE,
          ...(isReseller &&
            coverageAreaStore?.selectedCoverageArea?.id && {
              coverage_area_id: coverageAreaStore?.selectedCoverageArea?.id,
            }),
          ...(q && { name: String(q) }),
          ...(String(filter)?.toLowerCase() === "promo" && {
            promo_active: true,
          }),
          ...(String(filter)?.toLowerCase() === "new" && { is_new: true }),
          ...(category && { product_category_id: String(category) }),
          ...(fromapp &&
            !hasReferralCategory && { filtered_product_category: true }),
          ...(order_by && { order_by: String(order_by) }),
          ...(sort_by && { sort_by: String(sort_by) }),
        };
        if (isReseller && !trading) {
          await productStore?.getResellerProducts(params);
        } else {
          await productStore?.getProducts(params);
        }
        setIsFetching(false);
      } catch (err) {
        canLoadMore.current = false;
        setIsFetching(false);
      }
    }, [
      accountStore.buyerAccount,
      coverageAreaStore?.selectedCoverageArea?.id,
      isReseller,
      productStore,
      products.length,
      router.query,
    ]);

    const handleDockHeaderScroll = useCallback(
      (event) => {
        if (event.target.scrollTop > 10) {
          !isHeaderDocked && setIsHeaderDocked(true);
        } else {
          isHeaderDocked && setIsHeaderDocked(false);
        }
      },
      [isHeaderDocked]
    );

    const handleLoadMoreScroll = useCallback(
      (event) => {
        if (isFetching || !canLoadMore.current) {
          return;
        }
        const reachedBottom =
          event.target.scrollHeight -
            event.target.scrollTop -
            event.target.clientHeight <
          1;
        if (reachedBottom && canLoadMore.current) {
          handleLoadMore();
        }
      },
      [handleLoadMore, isFetching]
    );

    const handleScroll = useCallback(
      (event) => {
        handleDockHeaderScroll(event);
        handleLoadMoreScroll(event);
      },
      [handleDockHeaderScroll, handleLoadMoreScroll]
    );

    useEffect(() => {
      canLoadMore.current = products.length < productStore.productsCount;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products.length, productStore?.productsCount, isFetching]);

    const handleClickBuy = (product: Product) => {
      onClickBuy(product);

      if (sellerData?.sellerJopan) {
        trackEvents({
          sellerUsername: sellerData?.User?.username,
          eventName: "produk_klik_beli",
          eventCategory: "order",
          eventLabel: "product purchase click",
          eventValue: {
            productId: product?.id,
          },
          trackers: ["GTM", "AF", "ME"],
        });
      }
    };

    const renderGrid = () => {
      // separate odd/even indexes to render in masonry grid layout
      const productsArr = {
        odd: products.filter((_, index) => index % 2 === 0),
        even: products.filter((_, index) => index % 2 !== 0),
      };

      return (
        <div
          className={classNames(
            "w-full flex overflow-x-auto py-4 no-scrollbar animate-fade-in",
            {
              "mb-[50px]":
                !router.query.fromapp && cartStore?.cartItems?.length,
              "pb-[50px]": router.query.fromapp && cartStore?.cartItems?.length,
            }
          )}
          onScroll={throttle(handleScroll, 150)}
        >
          <div className="flex flex-col mr-3 w-1/2">
            {!!productsArr.odd.length &&
              productsArr.odd.map((product, index) => (
                <ProductGridItem
                  key={product.id}
                  sellerData={sellerData}
                  product={product}
                  containerClassName={
                    index + 1 === productsArr.odd.length
                      ? cartStore?.cartItems?.length
                        ? "pb-28"
                        : "pb-4 last:pb-[120px]"
                      : "mb-3"
                  }
                  onClickBuy={() => handleClickBuy(product)}
                />
              ))}
          </div>
          <div className="flex flex-col w-1/2">
            {!!productsArr.even.length &&
              productsArr.even.map((product, index) => (
                <ProductGridItem
                  key={product.id}
                  sellerData={sellerData}
                  product={product}
                  containerClassName={
                    index + 1 === productsArr.even.length
                      ? cartStore?.cartItems?.length
                        ? "pb-28"
                        : "pb-4 last:pb-[120px]"
                      : "mb-3"
                  }
                  onClickBuy={() => handleClickBuy(product)}
                />
              ))}
          </div>
        </div>
      );
    };

    const renderList = () => {
      return (
        <div
          className={classNames(
            "w-full overflow-y-auto py-4 no-scrollbar animate-fade-out",
            {
              "pb-[150px]": cartStore?.cartItems?.length,
              "pb-16": !cartStore?.cartItems?.length,
            }
          )}
          onScroll={throttle(handleScroll, 150)}
        >
          {!!products.length &&
            products.map((product) => (
              <ProductListItem
                key={product.id}
                product={product}
                sellerData={sellerData}
              />
            ))}
        </div>
      );
    };

    if (!products.length) {
      const { q, category, filter, areaId } = router.query;
      let titleMessage = "Tidak Ada Produk";
      let descMessage = "Toko ini belum memiliki produk untuk dijual";
      if (q || category || filter || areaId) {
        titleMessage = "Tidak Ada Produk";
        descMessage = `Produk ${q ? `"${q}"` : ""} tidak ditemukan`;
      }
      return (
        <div className="flex flex-col items-center w-full py-4">
          <div className="relative pt-4">
            <Image
              src={`/img/${process.env.NEXT_PUBLIC_APP_NAME}/empty-order.svg`}
              alt="empty"
              width="240"
              height="240"
            />
          </div>
          <div className="text-lg font-bold pt-2">{titleMessage}</div>
          <div className="pt-4">{descMessage}</div>
        </div>
      );
    }

    return mode === ProductLayout.list ? renderList() : renderGrid();
  }
);
